import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import config from '../config.js';

// Base API path from config
const API_BASE_URL = `${config.apiBasePath}/api/v1`;

/**
 * Refreshes the access token using the provided refresh token.
 * Stores the new token data in local storage.
 * @param {string} refreshToken - The refresh token.
 * @returns {Promise<string>} The new access token.
 */
async function refreshAccessToken(refreshToken) {
    try {
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: `${refreshToken}`,
            token_type: 'bearer',
        });
        localStorage.setItem('token', JSON.stringify(response.data));
        return response.data.access_token;
    } catch (error) {
        console.error('Token refresh failed:', error.response?.data || error);
        localStorage.removeItem('token'); // Clear token if refresh fails
        localStorage.removeItem('user');

        // You might want to redirect to login page here or throw a specific error
        // if(error.response?.status === 401) {
        // if (error.response?.status === 401 && error.response?.data?.detail === "401: Refresh token expired. Please log in again.")
        window.location.href = '/login'; // redirect to login page
        // }

        throw error;
    }
}

/**
 * Axios request interceptor for handling JWT tokens.
 * Attaches the access token to requests and refreshes it if expired.
 * @param {object} config - Axios request config.
 * @returns {Promise<object>} Modified Axios request config.
 */
async function localStorageTokenInterceptor(axiosConfig) {
    const tokenString = localStorage.getItem('token');
    if (tokenString) {
        const token = JSON.parse(tokenString);
        const decoded = jwtDecode(token.access_token);
        // decoded.exp is in seconds, Date.now() is in milliseconds
        const tokenValid = decoded.exp * 1000 > Date.now();

        if (tokenValid) {
            axiosConfig.headers['Authorization'] = `Bearer ${token.access_token}`;
        } else {
            try {
                const newAccessToken = await refreshAccessToken(token.refresh_token);
                axiosConfig.headers['Authorization'] = `Bearer ${newAccessToken}`;
            } catch (err) {
                // If refresh fails, let the request proceed without token or throw error to be handled by caller
                // (e.g., redirect to login)
                return Promise.reject(err);
            }
        }
    }
    return axiosConfig;
}

/**
 * FastAPIClient Class for interacting with the backend API.
 * Handles authentication, data fetching, and error handling.
 */
class FastAPIClient {
    constructor() {
        this.apiClient = this.getApiClient(); // Axios instance with interceptors
        this.confirm = this.confirm.bind(this);
        this.login = this.login.bind(this);
        this.signup = this.signup.bind(this);
        this.fetchUser = this.fetchUser.bind(this);
        this.logout = this.logout.bind(this);
        this.fetchData = this.fetchData.bind(this);
        this.postData = this.postData.bind(this);
    }

    /**
     * Creates and configures an Axios instance with base URL and interceptors.
     * @returns {object} Axios instance.
     */
    getApiClient() {
        const client = axios.create({ baseURL: API_BASE_URL });
        client.interceptors.request.use(localStorageTokenInterceptor, (err) => Promise.reject(err));
        return client;
    }

    /**
     * Confirms user registration.
     * @param {string} salt - Confirmation salt.
     * @returns {Promise<object>} API response data.
     */
    confirm(salt) {
        return this.apiClient.post('/auth/confirm', { salt }).then((res) => res.data);
    }

    /**
     * Verify user identity immediately after Login.
     * @param {integer} users_id - User's serial.
     * @param {string} code - Verification code.
     * @returns {Promise<object>} API response data.
     */
    verifyCode(users_id, code) {
        delete this.apiClient.defaults.headers.common['Authorization'];

        return this.apiClient.post('/auth/verify-login', { users_id: users_id, code: code }).then((response) => {
            localStorage.setItem('token', JSON.stringify(response.data));
            return this.fetchUser();
        });
    }

    /**
     * Logs in a user and fetches user data.
     * Expects form-data for username/password.
     * @param {string} username - User's email.
     * @param {string} password - User's password.
     * @returns {Promise<object>} User data.
     */
    login(username, password) {
        // Clear Authorization header if it was set by a previous interceptor run
        // This is important for /auth/login as it doesn't use JWT in the request.
        delete this.apiClient.defaults.headers.common['Authorization'];

        const form_data = new FormData();
        form_data.append('grant_type', 'password');
        form_data.append('username', username);
        form_data.append('password', password);

        return this.apiClient.post('/auth/login', form_data).then((response) => {
            return response
            // localStorage.setItem('token', JSON.stringify(response.data));
            // return this.fetchUser();
        });
    }

    /**
     * Fetches the currently authenticated user's details.
     * @returns {Promise<object>} User data.
     */
    fetchUser() {
        return this.apiClient.get('/auth/me').then(({ data }) => {
            localStorage.setItem('user', JSON.stringify(data));
            return data;
        });
    }

    /**
     * Registers a new business customer.
     * Payload includes fixed internal defaults.
     * @param {string} username - User's email.
     * @param {string} password - User's password.
     * @param {string} first_name - First part of the business name.
     * @param {string} [middle_name] - Middle part of the business name (optional).
     * @param {string} last_name - Last part of the business name.
     * @returns {Promise<object>} API response data (e.g., { msg: "Signup successful. Proceed to login" }).
     */
    // signup(username, password, first_name, middle_name, last_name) {
    signup(username, password, business_name, phone, btype, orgtype) {
        const registerData = {
            username,
            password,
            business_name,
            phone,
            btype,
            organization_type: orgtype,
            // first_name,
            // middle_name: middle_name || null, // Ensure null if optional and empty string
            // last_name,
            member_state: 3,
            member_type: "O",
            register_type: "A",
            profile_type: "B",
        };
        // console.log(registerData)
        // Expecting JSON payload for signup, so no FormData
        return this.apiClient.post('/auth/signup-new', registerData).then((res) => res.data);
    }

    /**
     * Logs out the user by revoking the refresh token and clearing local storage.
     */
    async logout() {
        const tokenString = localStorage.getItem('token');
        if (tokenString) {
            const token = JSON.parse(tokenString);
            try {
                // Attempt to revoke the refresh token on the server
                await axios.post(`${API_BASE_URL}/auth/revoke`, {
                    refresh_token: token.refresh_token,
                    token_type: 'bearer',
                });
            } catch (error) {
                console.error('Token revocation failed (might be expired or invalid):', error.response?.data || error);
                // Continue with local logout even if server revocation fails
            }
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    }

    /**
     * Fetches data from a given URL.
     * @param {string} url - API endpoint path.
     * @param {object} [params={}] - Query parameters.
     * @returns {Promise<object>} API response data.
     */
    async fetchData(url, params = {}) {
        try {
            const response = await this.apiClient.get(url, { params });
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error; // Re-throw to allow component to catch specific errors
        }
    }

    /**
     * Posts data to a given URL.
     * @param {string} url - API endpoint path.
     * @param {object} [data={}] - Request body data.
     * @param {object} [params={}] - Query parameters.
     * @returns {Promise<object>} API response data.
     */
    async postData(url, data = {}, params = {}) {
        try {
            const response = await this.apiClient.post(url, data, { params });
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error; // Re-throw to allow component to catch specific errors
        }
    }

    /**
     * Generic error handling for API responses.
     * @param {object} error - Axios error object.
     */
    handleError(error) {
        if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            const status = error.response.status;
            const data = error.response.data;
            let errorMessage = `Error ${status}`;

            if (data && data.detail) {
                if (typeof data.detail === 'string') {
                    errorMessage += `: ${data.detail}`;
                } else if (Array.isArray(data.detail)) {
                    // Handle validation errors or multiple details
                    errorMessage += `: ${data.detail.map(d => d.msg || d.message || d).join(', ')}`;
                }
            } else if (data && data.message) {
                errorMessage += `: ${data.message}`;
            }

            console.error('API Error:', errorMessage, error.response);
        } else if (error.request) {
            // The request was made but no response was received
            console.error('Network Error: No response received from server.', error.request);
            throw new Error('Network Error: Could not connect to the server. Please check your internet connection or try again later.');
        } else {
            // Something happened in setting up the request that triggered an Error
            console.error('Client Error:', error.message);
        }
        // Re-throw the original error to allow upstream components to handle it
        throw error;
    }
}

export default FastAPIClient;
